/**
 * vendor/isomorphic/apps/isomorphic/src/app/shared/ecommerce/dashboard/best-sellers.tsx — its
 * `WidgetCard` and thumbnail-title rows, fed by the first page of
 * `GET /api/customers/admin/customers?status=0` (pending_verification) instead of `topProducts`.
 * The count is the API's own `totalCount`; the rows are the first five in the order received.
 */
import { Link } from "react-router-dom";
import { WidgetCard } from "@steelyard/theme";
import { Badge, Text } from "rizzui";
import { PiUserCheckDuotone as UserCheck } from "react-icons/pi";
import { TileBoundary } from "./TileBoundary";
import { EmptyTile, countOf, UnreadableWidgetCard } from "./TrendStatCard";
import { basisLabel, type PagedCustomers } from "../../lib/services/customers";
import { customerPath } from "../../lib/paths";

const TITLE = "Customers awaiting approval";

export function PendingCustomersTile({ resolve }: { resolve: Promise<PagedCustomers> }) {
  const unreadable = (error: unknown) => (
    <UnreadableWidgetCard title={TITLE} error={error} fallback="Could not be read — this is not the same as nobody waiting." />
  );

  return (
    <TileBoundary resolve={resolve} errorFallback={unreadable} fallbackClassName="h-[380px]">
      {({ items, totalCount }) => (
        <WidgetCard
          title={
            <span className="flex items-center gap-2">
              {TITLE}
              <Badge variant="flat" color={totalCount === 0 ? "success" : "warning"} rounded="pill" size="sm">
                {totalCount}
              </Badge>
            </span>
          }
          description={`${countOf(totalCount, "registration")} pending verification`}
          descriptionClassName="mt-1 text-gray-500"
          className="flex flex-col"
          action={
            <Link to="/customers?status=0" className="whitespace-nowrap text-sm font-medium underline">
              View all
            </Link>
          }
        >
          {items.length === 0 ? (
            <EmptyTile icon={<UserCheck />} title="No registrations waiting.">
              New customers who register appear here until they are approved.
            </EmptyTile>
          ) : (
            <ul className="mt-[18px] grid grid-cols-1 gap-4 @sm:gap-5">
              {items.map((customer) => (
                <li key={customer.customerId} className="flex min-w-0 items-center pe-2">
                  <div className="me-3 flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-primary/10 font-lexend text-sm font-semibold text-primary @sm:h-12 @sm:w-12">
                    {customer.displayName.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex min-w-0 flex-1 items-center justify-between gap-3">
                    <div className="min-w-0">
                      <Text className="truncate font-lexend text-sm font-medium text-gray-900 dark:text-gray-700">
                        <Link to={customerPath(customer.customerNumber)} className="hover:underline">
                          {customer.displayName}
                        </Link>
                      </Text>
                      <Text className="text-gray-500">{basisLabel(customer)}</Text>
                    </div>
                    <Link to={customerPath(customer.customerNumber)} className="shrink-0 text-sm font-medium text-primary hover:underline">
                      Review
                    </Link>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </WidgetCard>
      )}
    </TileBoundary>
  );
}
